import { useEffect, useMemo, useState } from 'react'
import cn from 'classnames'
import { Button, Checkbox, Select, TextInput } from '@forwardflow/ui-v2'
import { MailIcon, PhoneIcon, User01Icon } from '@forwardflow/icons'
import { ADDITIONAL_SEAT_COST } from '@forwardflow/constants/onboarding.constants'
import { useGetFubUsers } from '@forwardflow/queries/onboarding.queries'
import { useSubscriptionPackageLists } from '@forwardflow/hooks/customHooks'
import { useOnboardingStoreV2 } from '@forwardflow/store/onboardingStoreV2'
import { TFubUser } from '@forwardflow/types/onboarding'
import { DEMO_FUB_USERS, isFreeFlow, isSandbox, useDevTools } from '../dev/devTools'
import { Card, LinkButton, Section, Skeleton, StepFooter, StepHeader } from '../shell/Card'
import { e164, isUsPhone, prettyPhone } from '../shell/format'
import { fullName, MAX_SEATS, pickOwner, seatCostFor, seatSummary } from './you-and-team'

/**
 * Step 2: who you are and who is on the team, from Follow Up Boss.
 *
 * The signed-in person's FUB user is shown first with their phone, which is
 * where leads are handed to them and what the calling area code is matched
 * to; the phone can be changed here. Every other FUB user is a row with a
 * checkbox and its seat cost. The plan is never asked for: one seat is Solo,
 * more is Team.
 */
export const YouAndTeam = () => {
   const { onboardingData, getNextStep, getPreviousStep, setInvitedUsers, setPricingData } =
      useOnboardingStoreV2()
   const { email, organizationId, isFUBConnected, invitedUsers, pricingData } = onboardingData

   // No organization id in the sandbox: the hook fetches whenever it has one.
   const { data: fubUsersData, isLoading } = useGetFubUsers(
      isSandbox() ? '' : organizationId ?? '',
      1000,
      1,
      isFUBConnected
   )
   const { data: subscriptionPackages } = useSubscriptionPackageLists({ page: 1, limit: 10 })

   const users: TFubUser[] = useMemo(() => {
      const loaded = fubUsersData?.data || []
      return isFreeFlow() && loaded.length === 0 ? DEMO_FUB_USERS : loaded
   }, [fubUsersData])

   const savedOwner = invitedUsers?.find((u) => u.isOwner) || null
   const [ownerId, setOwnerId] = useState<string | null>(
      savedOwner ? String(savedOwner.id) : null
   )
   const [choosingOwner, setChoosingOwner] = useState(false)
   const [memberIds, setMemberIds] = useState<Set<string>>(
      () =>
         new Set((invitedUsers || []).filter((u) => !u.isOwner).map((u) => String(u.id)))
   )
   const [phone, setPhone] = useState<string>(savedOwner?.phone || '')
   const [editingPhone, setEditingPhone] = useState(false)
   const [phoneDraft, setPhoneDraft] = useState('')

   // Match the signed-in person once the list arrives.
   useEffect(() => {
      if (ownerId || !users.length) return
      const found = pickOwner(users, email)
      if (found) {
         setOwnerId(String(found.id))
         if (!phone) setPhone(found.phone || '')
      }
   }, [users, email, ownerId, phone])

   const owner = users.find((u) => String(u.id) === ownerId) || savedOwner
   const isMatchedByEmail =
      Boolean(owner?.email) && (owner?.email || '').toLowerCase() === (email || '').toLowerCase()
   const teammates = users.filter((u) => String(u.id) !== ownerId)

   const seats = Math.min(MAX_SEATS, 1 + memberIds.size)
   const seatCost = seatCostFor(seats)

   const toggleMember = (id: string) => {
      setMemberIds((prev) => {
         const next = new Set(prev)
         if (next.has(id)) next.delete(id)
         else if (next.size + 1 < MAX_SEATS) next.add(id)
         return next
      })
   }

   const chooseOwner = (id: string | null) => {
      if (!id) return
      setOwnerId(id)
      setChoosingOwner(false)
      const picked = users.find((u) => String(u.id) === id)
      setPhone(picked?.phone || '')
      setMemberIds((prev) => {
         const next = new Set(prev)
         next.delete(id)
         return next
      })
   }

   const savePhone = () => {
      if (!isUsPhone(phoneDraft)) return
      setPhone(e164(phoneDraft))
      setEditingPhone(false)
   }

   const handleContinue = () => {
      const members = users.filter((u) => memberIds.has(String(u.id)))
      setInvitedUsers([
         ...members.map((u) => ({ ...u, isOwner: false })),
         ...(owner ? [{ ...owner, phone: phone || owner.phone, isOwner: true } as TFubUser] : []),
      ])
      const tierId =
         subscriptionPackages?.pricingTiers?.[0]?.id ?? pricingData?.selectedTierId ?? null
      setPricingData({ ...pricingData, seatsCount: 1 + members.length, selectedTierId: tierId })
      getNextStep()
   }

   useDevTools({
      fill: () => setMemberIds(new Set(teammates.slice(0, 2).map((u) => String(u.id)))),
      skip: () => {
         handleContinue()
         return true
      },
   })

   const phoneOk = isUsPhone(phone)
   const canContinue = isFreeFlow() || (Boolean(owner) && phoneOk)
   const ownerOptions = users.map((u) => ({
      value: String(u.id),
      label: `${fullName(u)} · ${u.email}`,
   }))

   return (
      <div className="space-y-4">
         <Card>
            <StepHeader
               title="Is this you?"
               lede={
                  isMatchedByEmail
                     ? 'We matched your Follow Up Boss user to your sign-in email. Interested leads are handed to this person, on this phone number.'
                     : 'Pick your own Follow Up Boss user. Interested leads are handed to this person, on this phone number.'
               }
            />

            {isLoading && !users.length ? (
               <Skeleton className="h-20" />
            ) : !owner || choosingOwner ? (
               <Select
                  searchable
                  value={ownerId}
                  data={ownerOptions}
                  placeholder="Search your Follow Up Boss users"
                  label="Your account"
                  onChange={chooseOwner}
                  comboboxProps={{ withinPortal: true }}
                  nothingFoundMessage={
                     users.length ? 'No user matches' : 'Still reading your team from Follow Up Boss'
                  }
               />
            ) : (
               <div
                  className="rounded-xl border border-[#9F7BFF] bg-[rgba(159,123,255,0.10)] p-4"
                  data-testid="owner-card"
               >
                  <div className="flex items-start gap-3">
                     <span className="flex shrink-0 items-center justify-center w-10 h-10 rounded-full bg-[#9F7BFF]/20 text-[#A684FF]">
                        <User01Icon width={20} height={20} />
                     </span>
                     <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-3">
                           <p className="text-white font-semibold text-sm truncate">
                              {fullName(owner)}
                              {owner.isOwner && (
                                 <span className="ml-2 text-[11px] font-medium text-[#A684FF]">
                                    account owner
                                 </span>
                              )}
                           </p>
                           <LinkButton onClick={() => setChoosingOwner(true)}>Not you?</LinkButton>
                        </div>
                        {owner.email && (
                           <p className="flex items-center gap-1.5 text-[#99A1AF] text-xs mt-1 min-w-0">
                              <MailIcon width={13} /> <span className="truncate">{owner.email}</span>
                           </p>
                        )}
                        <div className="mt-2.5 pt-2.5 border-t border-white/10">
                           {editingPhone ? (
                              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                                 <TextInput
                                    label="Your mobile number"
                                    description="Leads are transferred to this number. US numbers only."
                                    placeholder="(719) 555-0100"
                                    value={phoneDraft}
                                    onChange={(e) => setPhoneDraft(e.currentTarget.value)}
                                    onKeyDown={(e) => {
                                       if (e.key === 'Enter') savePhone()
                                    }}
                                    inputMode="tel"
                                    autoFocus
                                    className="flex-1"
                                    data-testid="owner-phone-input"
                                 />
                                 <div className="flex gap-2">
                                    <Button
                                       size="middle"
                                       className="!h-10"
                                       onClick={savePhone}
                                       disabled={!isUsPhone(phoneDraft)}
                                    >
                                       Save
                                    </Button>
                                    <Button
                                       size="middle"
                                       variant="secondary"
                                       className="!h-10"
                                       onClick={() => setEditingPhone(false)}
                                    >
                                       Cancel
                                    </Button>
                                 </div>
                              </div>
                           ) : (
                              <div className="flex items-center justify-between gap-3">
                                 <p className="flex items-center gap-1.5 text-sm min-w-0">
                                    <PhoneIcon width={14} color="#A684FF" />
                                    {phone ? (
                                       <span className="text-white" data-testid="owner-phone">
                                          {prettyPhone(phone)}
                                       </span>
                                    ) : (
                                       <span className="text-white/50">No phone number yet</span>
                                    )}
                                    {phone && !phoneOk && (
                                       <span className="text-white/50 text-xs">· not a US number</span>
                                    )}
                                 </p>
                                 <LinkButton
                                    onClick={() => {
                                       setPhoneDraft(phone ? prettyPhone(phone) : '')
                                       setEditingPhone(true)
                                    }}
                                    testId="change-phone"
                                 >
                                    {phone ? 'Change number' : 'Add a number'}
                                 </LinkButton>
                              </div>
                           )}
                        </div>
                     </div>
                  </div>
               </div>
            )}
         </Card>

         <Card delay={0.08}>
            <StepHeader
               title="Who else should get leads?"
               lede={`Tick the teammates we should hand leads to as well. Each one is $${ADDITIONAL_SEAT_COST}/mo. Skip this if it's just you.`}
            />

            {isLoading && !users.length ? (
               <div className="space-y-2">
                  <Skeleton className="h-12" />
                  <Skeleton className="h-12" />
               </div>
            ) : teammates.length === 0 ? (
               <p className="text-white/50 text-sm">
                  No other users in your Follow Up Boss account. Add seats any time under
                  Settings → Users.
               </p>
            ) : (
               <ul className="space-y-1.5" data-testid="teammate-list">
                  {teammates.map((u) => {
                     const id = String(u.id)
                     const checked = memberIds.has(id)
                     return (
                        <li key={id}>
                           <label
                              className={cn(
                                 'flex items-center gap-3 rounded-xl border px-3.5 py-2.5 cursor-pointer transition-all',
                                 checked
                                    ? 'border-[#9F7BFF]/60 bg-[rgba(159,123,255,0.08)]'
                                    : 'border-white/10 bg-white/[0.02] hover:border-white/25'
                              )}
                           >
                              <Checkbox size="sm" checked={checked} onChange={() => toggleMember(id)} />
                              <div className="flex-1 min-w-0">
                                 <p className="text-white text-sm font-medium truncate">{fullName(u)}</p>
                                 <p className="text-[#99A1AF] text-xs truncate">{u.email}</p>
                              </div>
                              <span
                                 className={cn(
                                    'text-xs font-medium',
                                    checked ? 'text-white' : 'text-white/40'
                                 )}
                              >
                                 +${ADDITIONAL_SEAT_COST}/mo
                              </span>
                           </label>
                        </li>
                     )
                  })}
               </ul>
            )}

            <Section>
               <div className="rounded-xl border border-[#9F7BFF]/30 bg-[#9F7BFF]/[0.08] px-4 py-3 flex items-center justify-between gap-4">
                  <div>
                     <p className="text-white/80 text-sm">{seatSummary(seats)}</p>
                     <p className="text-white/40 text-xs mt-0.5">
                        Teammates get an invite once you launch. Add or remove seats any time.
                     </p>
                  </div>
                  <p className="text-white font-semibold text-lg whitespace-nowrap" data-testid="seat-cost">
                     ${seatCost}/mo
                  </p>
               </div>
            </Section>
         </Card>

         <StepFooter
            onBack={() => getPreviousStep()}
            action={
               canContinue
                  ? `Continue with ${seats} ${seats === 1 ? 'seat' : 'seats'} · $${seatCost}/mo`
                  : !owner
                    ? 'Pick your account to continue'
                    : 'Add your mobile number to continue'
            }
            onAction={handleContinue}
            disabled={!canContinue}
            note="Next: how much to spend on calls each month."
            testId="continue-team"
         />
      </div>
   )
}
